import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import ReportsCoverPagelessComponent from 'webapp/components/reports/cover-pageless';
import { OrderProperty } from 'webapp/components/reports/editor-base';
import ReportsFragmentGridComponent from 'webapp/components/reports/fragment-grid/index';
import ReportsPagelessBottomInfoComponent from 'webapp/components/reports/pageless-bottom-info';
import ReportsViewerFragmentBlockComponent from 'webapp/components/reports/viewer/fragment/block';
import ReportsViewerFragmentBoardComponent from 'webapp/components/reports/viewer/fragment/board';
import ReportsViewerFragmentImageComponent from 'webapp/components/reports/viewer/fragment/image';
import ReportsViewerFragmentStoryTextComponent from 'webapp/components/reports/viewer/fragment/story-text';
import ReportsViewerFragmentTitleComponent from 'webapp/components/reports/viewer/fragment/title';

import Board from 'webapp/models-custom/board';
import type Report from 'webapp/models-custom/report';

interface Args {
	model?: Report | null; // can be missing because of public report template - getting report data via token endpoint
	isLoaderShown?: boolean;
	onBoardPreviewHashChange?: (boardId: string, previewHash?: string) => void;
}

export default class ReportsViewerPagelessComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	node: HTMLElement | null = null;

	@tracked contentWidth = 0;
	@tracked wrapperWidth = 0;

	orderProperty = OrderProperty.GLOBALORDER;

	get bgTheme() {
		return this.navigationService.theme.id;
	}

	get fontSize(): number {
		return Board.getFontsizeFromWidth(this.contentWidth);
	}

	get reportsFragments() {
		return this.args.model?.shownFragments ?? [];
	}

	get hideBranding(): boolean {
		return this.accountService.removeBrandingOnFeatures;
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;
		this.updateSizes();
		$(window).on('resize.datawallcenter', this.debounceUpdateSizes);
	}

	@action
	teardownView(): void {
		$(window).off('resize.datawallcenter', this.debounceUpdateSizes);
	}

	@action
	debounceUpdateSizes() {
		debounce(this, this.updateSizes, 100);
	}

	@action
	updateSizes(): void {
		const content = this.node?.querySelector<HTMLElement>('.pageless-content'),
			wrapper = content?.closest<HTMLElement>('.pageless-wrapper');
		if (!content || !wrapper) {
			return;
		}

		this.contentWidth = content.offsetWidth;
		this.wrapperWidth = wrapper.offsetWidth;
		// to match font size on slides
		if (this.node) {
			this.node.style.fontSize = `${this.fontSize}px`;
		}
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		node.dispatchEvent(new Event(containerEventName));
	}
	<template>
		<div
			id="report-viewer_body"
			class="report_ct report_ct--pageless {{if @model.isEmpty 'report_ct--empty'}}"
			{{visibleCheckContainer true}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			{{#if @isLoaderShown}}
				<div class="bar-container">
					<BarLoaderComponent @size="big" />
				</div>
			{{else if @model}}
				<div class="pageless-wrapper bgTheme-light colorTheme-1">
					{{#if @model.cover}}
						<ReportsCoverPagelessComponent @cover={{@model.cover}} />
					{{/if}}
					<div class="pageless-content">
						<ReportsFragmentGridComponent
							@report={{@model}}
							@model={{this.reportsFragments}}
							@orderProperty={{this.orderProperty}}
							as |fragments Item|
						>
							{{#each fragments as |fragment|}}
								<Item @model={{fragment}}>
									{{#if fragment.isTypeBoard}}
										<ReportsViewerFragmentBoardComponent
											@model={{fragment}}
											@onBoardPreviewHashChange={{@onBoardPreviewHashChange}}
											@width={{this.wrapperWidth}}
										/>
									{{else if fragment.isTypeImage}}
										<ReportsViewerFragmentImageComponent @model={{fragment}} />
									{{else if fragment.isTypeBlock}}
										<ReportsViewerFragmentBlockComponent @model={{fragment}} />
									{{else if fragment.isTypeStoryText}}
										<ReportsViewerFragmentStoryTextComponent
											@model={{fragment}}
										/>
									{{else if fragment.isTypeTitle}}
										<ReportsViewerFragmentTitleComponent @model={{fragment}} />
									{{/if}}
								</Item>
							{{/each}}
						</ReportsFragmentGridComponent>
					</div>

					<ReportsPagelessBottomInfoComponent
						@model={{@model}}
						@hideBranding={{this.hideBranding}}
					/>
				</div>
			{{/if}}
		</div>
	</template>
}
